import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'

import { STEPS_ORDER_BOOK } from '../../../../utils/constants'
import { COOKBOOK_SIZES, cookbookSize } from '../../../../utils/cookbookSizes'
import { getCookbook, saveCookbook, saveCookbookCover } from '../../../../api/cookbook'
import CustomInputFile from '../../../../components/CustomInputFile'
import InputSimple from '../../../../components/InputSimple'
import CustomTitle from '../CustomTitle'
import Footer from '../Footer'

import styles from './ThirdStep.module.css'

const MAX_DEDICATION = 300

const THEMES = [
  { value: 'classic', name: 'Classic', detail: 'Morsel’s own look: warm brown, cream and green.' },
  { value: 'modern', name: 'Modern', detail: 'Clean and bright, with big photos and bold type.' },
  {
    value: 'heirloom',
    name: 'Heirloom',
    detail: 'A keepsake: classic serif type on warm, paper-toned pages.',
  },
]

const PRODUCTS = [
  {
    value: 'coil',
    name: 'Kitchen Edition',
    detail: 'Coil bound, lays flat on the counter. The everyday book you cook from.',
  },
  {
    value: 'hardcover',
    name: 'Heirloom Edition',
    detail: 'Hardcover with the title on the spine. The gift book.',
  },
]

// Size choices with a small page outline showing the shape
const SIZES = COOKBOOK_SIZES.map((s) => ({
  value: s.value,
  name: s.name,
  detail: `${s.label}. ${s.detail}`,
  shape: s.ratio,
}))

const TEXT_FIELDS = [
  {
    name: 'title',
    title: 'Title',
    placeholder: 'The Hoyt Family Kitchen',
    rules: {
      required: 'Give your cookbook a title',
      maxLength: { value: 60, message: 'Keep it under 60 characters' },
    },
  },
  {
    name: 'subtitle',
    title: 'Subtitle',
    optional: true,
    placeholder: 'Recipes worth passing down',
    rules: { maxLength: { value: 120, message: 'Keep it under 120 characters' } },
  },
  {
    name: 'author_line',
    title: 'Author line',
    optional: true,
    placeholder: 'From the Hoyt family',
    rules: { maxLength: { value: 120, message: 'Keep it under 120 characters' } },
  },
]

const ThirdStep = ({ orderUrlData }) => {
  const { control, handleSubmit, reset, watch } = useForm()
  const [coverPreview, setCoverPreview] = useState('')
  const [cover, setCover] = useState('')
  const [coverError, setCoverError] = useState('')
  const buttonRef = useRef(null)
  const navigate = useNavigate()

  const { orderId } = orderUrlData
  const dedication = watch('dedication') || ''
  const [title, subtitle, authorLine, theme, product, trimSize] = watch([
    'title',
    'subtitle',
    'author_line',
    'theme',
    'product',
    'trim_size',
  ])

  const onSubmit = (data) => {
    buttonRef.current.disabled = true
    saveCookbook({ id: orderId, ...data })
      .then(() => {
        navigate('.', { state: { ...orderUrlData, currentStep: STEPS_ORDER_BOOK['PREVIEW_STEP'] } })
      })
      .catch(() => (buttonRef.current.disabled = false))
  }

  // The cover photo is saved as soon as it's chosen
  const uploadCover = (file) => {
    if (!file) return
    setCoverError('')
    saveCookbookCover(orderId, file).then((result) => {
      if (!result?.cover_photo) setCoverError('That photo could not be used. Try a JPG or PNG.')
    })
  }

  useEffect(() => {
    getCookbook(orderId).then(({ data }) => {
      reset({
        title: data.title || '',
        subtitle: data.subtitle || '',
        author_line: data.author_line || '',
        dedication: data.dedication || '',
        intro: data.intro || '',
        product: data.product || 'coil',
        theme: data.theme || 'classic',
        trim_size: data.trim_size || 'square',
      })
      // No uploaded cover yet: the book uses the first recipe photo
      if (data.cover_photo && !data.cover_photo.endsWith('imageRecipeWithoutPhoto.png')) {
        setCover(data.cover_photo)
      }
    })
  }, [])

  const renderChoices = (name, options, defaultValue) => (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => (
        <div className={`cbChoices ${styles[`choices_${name}`]}`} role="radiogroup">
          {options.map((option) => (
            <label
              key={option.value}
              className={`cbChoice ${value === option.value ? 'cbChoiceOn' : ''}`}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
              />
              <span className="cbChoiceDot" />
              <span className="cbChoiceBody">
                {option.shape ? (
                  <span className={styles.shapeBox} aria-hidden="true">
                    <span
                      className={styles.shape}
                      style={{ width: `${Math.round(40 * option.shape)}px` }}
                    />
                  </span>
                ) : null}
                {option.swatch ? (
                  <span className={`${styles.swatch} ${styles[`swatch_${option.value}`]}`}>
                    <span>Aa</span>
                  </span>
                ) : null}
                <b>{option.name}</b>
                {option.detail}
              </span>
            </label>
          ))}
        </div>
      )}
    />
  )

  const coverImage = coverPreview || cover

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="cbStep">
      <div className={styles.layout}>
        <div className={styles.main}>
          <section className="cbSection">
            <CustomTitle
              title="The cover"
              hoverText="What goes on the front of your book and on the spine."
            />
            {TEXT_FIELDS.map((field) => (
              <Controller
                key={field.name}
                name={field.name}
                rules={field.rules}
                control={control}
                defaultValue=""
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <div>
                    <label className="cbLabel" htmlFor={`cookbook-${field.name}`}>
                      {field.title}
                      {field.optional ? <span className="cbLabelHint">optional</span> : null}
                    </label>
                    <InputSimple
                      controlIdInput={`cookbook-${field.name}`}
                      value={value}
                      onChange={onChange}
                      error={error}
                      nameInputPlaceholder={field.placeholder}
                    />
                  </div>
                )}
              />
            ))}
            <div>
              <p className="cbLabel">
                Cover photo<span className="cbLabelHint">optional</span>
              </p>
              <p className="cbHint">
                Use a large, sharp JPG or PNG. Without one, the cover uses your first recipe photo.
              </p>
              <CustomInputFile
                value={cover}
                isAlwaysShow
                onChange={uploadCover}
                setExternal={setCoverPreview}
                idName="cookbook-cover"
                buttonClass={styles.uploadButton}
                textButton={coverImage ? 'Change photo' : 'Upload a cover photo'}
                classInner={styles.upload}
              />
              {coverError ? <p className="cbError">{coverError}</p> : null}
            </div>
          </section>

          <section className="cbSection">
            <CustomTitle
              title="Inside the book"
              hoverText="Both pages are optional. Leave them blank to skip them."
            />
            <Controller
              name="dedication"
              rules={{
                maxLength: {
                  value: MAX_DEDICATION,
                  message: `Keep it under ${MAX_DEDICATION} characters`,
                },
              }}
              control={control}
              defaultValue=""
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <div>
                  <label className="cbLabel" htmlFor="cookbook-dedication">
                    Dedication
                    <span className="cbLabelHint">a short line on its own page</span>
                  </label>
                  <InputSimple
                    controlIdInput="cookbook-dedication"
                    value={value}
                    onChange={onChange}
                    as="textarea"
                    error={error}
                    nameInputPlaceholder="For Grandma June, who never measured anything."
                    styleInput={styles.dedication}
                  />
                  <p className={styles.counter}>
                    {dedication.length} / {MAX_DEDICATION}
                  </p>
                </div>
              )}
            />
            <Controller
              name="intro"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <div>
                  <label className="cbLabel" htmlFor="cookbook-intro">
                    Foreword
                    <span className="cbLabelHint">a page or two about the book</span>
                  </label>
                  <InputSimple
                    controlIdInput="cookbook-intro"
                    value={value}
                    onChange={onChange}
                    as="textarea"
                    nameInputPlaceholder="How these recipes came together, and why they matter to your family."
                    styleInput={styles.foreword}
                  />
                </div>
              )}
            />
          </section>

          <section className="cbSection">
            <CustomTitle
              title="Design"
              hoverText="How the pages and cover look. Switch and preview as often as you like."
            />
            {renderChoices(
              'theme',
              THEMES.map((t) => ({ ...t, swatch: true })),
              'classic'
            )}
          </section>

          <section className="cbSection">
            <CustomTitle
              title="Binding"
              hoverText="Both are full color and come in every size below."
            />
            {renderChoices('product', PRODUCTS, 'coil')}
          </section>

          <section className="cbSection">
            <CustomTitle
              title="Size"
              hoverText="The size of each page. Every size comes in both bindings and all three designs."
            />
            {renderChoices('trim_size', SIZES, 'square')}
          </section>
        </div>

        <aside className={styles.side}>
          <div className={styles.sketchCard}>
            <p className={styles.sketchLabel}>Cover sketch</p>
            <div
              className={`${styles.sketch} ${styles[`sketch_${theme || 'classic'}`]}`}
              style={{ aspectRatio: String(cookbookSize(trimSize).ratio) }}
            >
              {coverImage ? <img src={coverImage} alt="" className={styles.sketchPhoto} /> : null}
              <div className={styles.sketchText}>
                <p className={styles.sketchTitle}>{title || 'Your cookbook title'}</p>
                {subtitle ? <p className={styles.sketchSubtitle}>{subtitle}</p> : null}
                {authorLine ? <p className={styles.sketchAuthor}>{authorLine}</p> : null}
              </div>
              {product === 'coil' ? <span className={styles.sketchCoil} /> : null}
            </div>
            <p className="cbMuted">
              A rough idea only. You’ll see every real page, cover included, in the next step.
            </p>
          </div>
        </aside>
      </div>

      <Footer backTo={-1} submitButtonRef={buttonRef} buttonText="Next: preview the book" />
    </form>
  )
}

export default ThirdStep
